import clsx from 'clsx'
import { Brain, MessageCircleQuestion, ThumbsDown, ThumbsUp } from 'lucide-react'
import { type ReactNode, useId, useReducer } from 'react'
import { Link } from 'react-router-dom'
import { Button, Panel, Textarea } from '@/components/ui/primitives.tsx'
import { useSendMessageFeedback, useShareQuestion } from '@/hooks/use-engagement.ts'
import { useI18n } from '@/i18n/index.ts'
import {
  feedbackReducer,
  initialFeedbackState,
  NOTE_MAX,
  QUESTION_MAX,
  showsShareOffer,
  type Vote
} from './feedback-state.ts'

/**
 * Thumbs on a finished answer, plus the two things a thumb can lead to (AGM-120/121).
 *
 * Only finished answers get this row: rating something still being written rates a sentence
 * that has not arrived, and the version the feedback is attributed to is the one that
 * produced the whole answer.
 *
 * The share offer appears on a thumbs-down and only for an agent with knowledge, because
 * that is the case where the owner can actually act — a question their agent could not
 * answer from its documents is a gap they can fill. It is never sent automatically and never
 * carries the answer: the person edits their own question and presses send, or does not.
 *
 * The memory-influence disclosure rides in this same row rather than in a line of its own
 * (AGM-092). The row already exists under every finished answer at a fixed height, so an
 * answer that used saved preferences and one that did not occupy exactly the same space —
 * an indicator on its own line would nudge the conversation down as each answer settled.
 */
export const MessageFeedback = ({
  messageId,
  conversationId,
  agentId,
  agentHasKnowledge,
  question,
  runId,
  initialVote,
  memoryInfluenced
}: {
  messageId: string
  conversationId: string | undefined
  agentId: string | undefined
  agentHasKnowledge: boolean
  /** The user turn this answer replied to — the prefill for the share panel, never sent on its own. */
  question: string
  runId: string | null
  initialVote: Vote | null
  /** True when the reader's saved preferences entered this answer's context (AGM-092). */
  memoryInfluenced: boolean
}) => {
  // Inside the body, never at module scope: a capture taken at import freezes the locale and
  // then renders the previous language after a switch, with no error anywhere (PRD-27 F-3).
  const t = useI18n().t.feedback
  const [state, dispatch] = useReducer(feedbackReducer, initialVote, initialFeedbackState)
  const sendFeedback = useSendMessageFeedback(conversationId)
  const share = useShareQuestion(agentId ?? '')
  // Every answer in the conversation renders one of these, so the field ids have to be
  // unique per row or the labels all point at the first note box on the page.
  const fieldId = useId()

  const vote = (next: Vote) => {
    if (state.voteStatus === 'sending' || state.vote === next) return
    dispatch({ type: 'vote', vote: next })
    sendFeedback.mutate(
      { messageId, helpful: next === 'up' },
      { onSuccess: () => dispatch({ type: 'vote_ok' }), onError: () => dispatch({ type: 'vote_failed' }) }
    )
  }

  const sendNote = () => {
    if (state.vote === null || state.note.trim() === '') return
    dispatch({ type: 'note_send' })
    sendFeedback.mutate(
      { messageId, helpful: state.vote === 'up', note: state.note.trim() },
      { onSuccess: () => dispatch({ type: 'note_ok' }), onError: () => dispatch({ type: 'note_failed' }) }
    )
  }

  const sendQuestion = () => {
    if (!agentId || state.question.trim() === '') return
    dispatch({ type: 'share_send' })
    share.mutate(
      { question: state.question.trim(), ...(runId ? { runId } : {}) },
      { onSuccess: () => dispatch({ type: 'share_ok' }), onError: () => dispatch({ type: 'share_failed' }) }
    )
  }

  const offerShare = Boolean(agentId) && showsShareOffer(state, agentHasKnowledge)

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {memoryInfluenced && (
          // Icon plus words, never a coloured dot: a reader who cannot distinguish the tint
          // still has to be able to learn that memory shaped this answer.
          <Link
            to={agentId ? `/memory?agent=${encodeURIComponent(agentId)}` : '/memory'}
            aria-label={t.memoryInfluencedLabel}
            className="readout mr-1 inline-flex items-center gap-1 text-[10px] text-fg-subtle hover:text-fg hover:underline"
          >
            <Brain size={11} aria-hidden />
            {t.memoryInfluenced}
          </Link>
        )}

        <ThumbButton
          pressed={state.vote === 'up'}
          tone="grounded"
          label={t.helpfulLabel}
          busy={state.voteStatus === 'sending'}
          onClick={() => vote('up')}
        >
          <ThumbsUp size={13} strokeWidth={1.9} aria-hidden />
        </ThumbButton>
        <ThumbButton
          pressed={state.vote === 'down'}
          tone="alert"
          label={t.unhelpfulLabel}
          busy={state.voteStatus === 'sending'}
          onClick={() => vote('down')}
        >
          <ThumbsDown size={13} strokeWidth={1.9} aria-hidden />
        </ThumbButton>

        {state.vote !== null && state.notePanel === 'closed' && (
          <button type="button" onClick={() => dispatch({ type: 'note_open' })} className={QUIET_CHIP}>
            {t.addNote}
          </button>
        )}

        {offerShare && (
          <button type="button" onClick={() => dispatch({ type: 'share_open', question })} className={QUIET_CHIP}>
            <MessageCircleQuestion size={12} aria-hidden />
            {t.shareOffer}
          </button>
        )}

        {/* One polite region for both confirmations: a screen reader hears the outcome of the
            click it just made, and never two announcements racing each other. */}
        <span role="status" className="readout text-[10px] text-fg-subtle">
          {state.voteStatus === 'failed'
            ? t.voteFailed
            : state.notePanel === 'sent'
              ? t.noteSent
              : state.vote !== null && state.voteStatus === 'idle'
                ? t.recorded
                : ''}
        </span>
      </div>

      {(state.notePanel === 'open' || state.notePanel === 'sending' || state.notePanel === 'failed') && (
        <NotePanel
          fieldId={`${fieldId}-note`}
          value={state.note}
          busy={state.notePanel === 'sending'}
          failed={state.notePanel === 'failed'}
          onChange={(value) => dispatch({ type: 'note_change', value })}
          onSend={sendNote}
          onCancel={() => dispatch({ type: 'note_close' })}
        />
      )}

      {state.sharePanel !== 'closed' && (
        <SharePanel
          fieldId={`${fieldId}-question`}
          value={state.question}
          status={state.sharePanel}
          onChange={(value) => dispatch({ type: 'share_change', value })}
          onSend={sendQuestion}
          onCancel={() => dispatch({ type: 'share_close' })}
        />
      )}
    </div>
  )
}

/** The quiet mono chips beside the thumbs: an offer, never a demand. */
const QUIET_CHIP =
  'readout inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] text-fg-subtle transition-colors hover:bg-card hover:text-fg'

/**
 * A thumb.
 *
 * Circular and hairlined like every other 29px control in the product, and tinted only once
 * it has been pressed — an unpressed pair that already wore its colours would read as a
 * verdict the reader had not given.
 */
const ThumbButton = ({
  pressed,
  tone,
  label,
  busy,
  onClick,
  children
}: {
  pressed: boolean
  tone: 'grounded' | 'alert'
  label: string
  busy: boolean
  onClick: () => void
  children: ReactNode
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={pressed}
    aria-label={label}
    disabled={busy}
    className={clsx(
      'inline-flex h-[29px] w-[29px] items-center justify-center rounded-full border',
      'transition-[color,background-color,border-color] duration-200 disabled:pointer-events-none disabled:opacity-45',
      !pressed && 'border-rule text-fg-muted',
      !pressed && tone === 'grounded' && 'hover:border-grounded hover:bg-grounded-soft hover:text-grounded',
      !pressed && tone === 'alert' && 'hover:border-alert hover:bg-alert-soft hover:text-alert',
      pressed && tone === 'grounded' && 'border-grounded bg-grounded-soft text-grounded',
      pressed && tone === 'alert' && 'border-alert bg-alert-soft text-alert'
    )}
  >
    {children}
  </button>
)

const NotePanel = ({
  fieldId,
  value,
  busy,
  failed,
  onChange,
  onSend,
  onCancel
}: {
  fieldId: string
  value: string
  busy: boolean
  failed: boolean
  onChange: (value: string) => void
  onSend: () => void
  onCancel: () => void
}) => {
  // Inside the body, never at module scope: a capture taken at import freezes the locale and
  // then renders the previous language after a switch with no error anywhere (PRD-27 F-3).
  const i18n = useI18n()
  const t = i18n.t.feedback

  return (
    <Panel solid className="mt-3 px-3.5 py-3">
      <label htmlFor={fieldId} className="eyebrow mb-1 block">
        {t.noteLabel}
      </label>
      <Textarea
        id={fieldId}
        rows={3}
        value={value}
        maxLength={NOTE_MAX}
        placeholder={t.notePlaceholder}
        disabled={busy}
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-fg-muted">{t.noteHint}</p>

      {failed && (
        <p role="alert" className="mt-1.5 text-[12.5px] text-alert">
          {t.noteFailed}
        </p>
      )}

      <div className="mt-2.5 flex items-center gap-2">
        <Button size="sm" variant="primary" disabled={busy || value.trim() === ''} onClick={onSend}>
          {busy ? t.noteSending : t.noteSend}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          {t.noteCancel}
        </Button>
        <span className="readout ml-auto text-fg-subtle">{t.noteRemaining(i18n, NOTE_MAX - value.length)}</span>
      </div>
    </Panel>
  )
}

const SharePanel = ({
  fieldId,
  value,
  status,
  onChange,
  onSend,
  onCancel
}: {
  fieldId: string
  value: string
  status: 'open' | 'sending' | 'sent' | 'failed'
  onChange: (value: string) => void
  onSend: () => void
  onCancel: () => void
}) => {
  const t = useI18n().t.feedback

  if (status === 'sent') {
    return (
      <Panel solid className="mt-3 px-3.5 py-2.5">
        <p role="status" className="text-[13px] text-grounded">
          {t.shareSent}
        </p>
      </Panel>
    )
  }

  return (
    <Panel solid className="mt-3 px-3.5 py-3">
      <h3 className="text-[13.5px]">{t.shareTitle}</h3>
      <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">{t.shareHint}</p>

      <label htmlFor={fieldId} className="eyebrow mt-2.5 mb-1 block">
        {t.shareLabel}
      </label>
      <Textarea
        id={fieldId}
        rows={3}
        value={value}
        maxLength={QUESTION_MAX}
        disabled={status === 'sending'}
        onChange={(event) => onChange(event.target.value)}
      />

      {status === 'failed' && (
        <p role="alert" className="mt-1.5 text-[12.5px] text-alert">
          {t.shareFailed}
        </p>
      )}

      <div className="mt-2.5 flex items-center gap-2">
        <Button size="sm" variant="primary" disabled={status === 'sending' || value.trim() === ''} onClick={onSend}>
          {status === 'sending' ? t.shareSending : t.shareSend}
        </Button>
        <Button size="sm" variant="ghost" disabled={status === 'sending'} onClick={onCancel}>
          {t.shareCancel}
        </Button>
      </div>
    </Panel>
  )
}
